import { Component, ElementRef, OnDestroy, OnInit, ViewChild } from '@angular/core';
import cloneDeep from 'lodash/cloneDeep';
import { ConfirmationService, MessageService } from 'primeng/api';
import { Subject, Subscription } from 'rxjs';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import {
  ContentReportExpertsService,
  ExpertAssignment,
  ExpertUser,
  OuExpertsOverview,
} from 'src/app/services/content-report-experts.service';
import { GlobalConstants } from 'src/app/services/global-constants';
import { PermissionHandlerService } from 'src/app/services/permission-handler.service';
import { TranslatorPipe } from 'src/app/services/translator.pipe';

/** The same OU subset the Create Report modal offers. */
const REPORT_OU_TAG = 'Ministries/Entities';

interface OuRow {
  _id: string;
  label: string;
  image: string;
  path: string;
  depth: number;
  assigned: ExpertAssignment[];
  suggested: Array<ExpertUser & { isAssigned: boolean }>;
  /** Lower-cased OU name + expert names/emails, for the search box. */
  searchText: string;
}

type CoverageFilter = 'all' | 'covered' | 'uncovered';

@Component({
  selector: 'app-content-experts-management',
  templateUrl: './content-experts-management.component.html',
  styleUrl: './content-experts-management.component.scss',
  providers: [ConfirmationService],
})
export class ContentExpertsManagementComponent implements OnInit, OnDestroy {
  @ViewChild('detailPanel') detailPanel?: ElementRef<HTMLElement>;

  staticURL = GlobalConstants.staticFilesURL;
  translator = new TranslatorPipe();

  loading = true;
  loadError = false;
  /** CONTENT_EXPERTS_MANAGEMENT: c = assign, u = change an expert, d = remove. */
  canAdd = false;
  canChange = false;
  canRemove = false;

  ous: OuRow[] = [];
  filteredOus: OuRow[] = [];
  selectedOu: OuRow | null = null;

  ouSearch = '';
  coverageFilter: CoverageFilter = 'all';
  coverageOptions: Array<{ label: string; value: CoverageFilter }> = [];

  // Add / change expert dialog
  pickerVisible = false;
  /** Set when the dialog replaces an existing assignment rather than adding. */
  pickerReplacing: ExpertAssignment | null = null;
  pickerQuery = '';
  pickerResults: ExpertUser[] = [];
  pickerSelected: ExpertUser[] = [];
  pickerSearching = false;
  pickerSearchError = false;
  private pickerSearch$ = new Subject<string>();
  private pickerRequestId = 0;
  private subscriptions = new Subscription();

  /** Keys of actions in flight, so repeated clicks do nothing. */
  busy = new Set<string>();

  constructor(
    private expertsService: ContentReportExpertsService,
    private permissionService: PermissionHandlerService,
    private messageService: MessageService,
    private confirmationService: ConfirmationService,
  ) {}

  ngOnInit(): void {
    this.canAdd = this.permissionService.hasPermission('CONTENT_EXPERTS_MANAGEMENT', 'c');
    this.canChange = this.permissionService.hasPermission('CONTENT_EXPERTS_MANAGEMENT', 'u');
    this.canRemove = this.permissionService.hasPermission('CONTENT_EXPERTS_MANAGEMENT', 'd');
    this.coverageOptions = [
      { label: this.t('All units'), value: 'all' },
      { label: this.t('Has experts'), value: 'covered' },
      { label: this.t('No experts'), value: 'uncovered' },
    ];
    this.subscriptions.add(
      this.pickerSearch$.pipe(debounceTime(300), distinctUntilChanged()).subscribe((q) => this.runUserSearch(q)),
    );
    this.load();
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  t(key: string): string {
    return this.translator.transform(key);
  }

  /** Translates a whole sentence, then fills its {placeholders}. */
  tf(key: string, values: Record<string, string | number>): string {
    return this.t(key).replace(/\{(\w+)\}/g, (_, name) => String(values[name] ?? ''));
  }

  // ---------------------------------------------------------------------------
  // Loading
  // ---------------------------------------------------------------------------

  async load(keepSelection = false): Promise<void> {
    const previousId = keepSelection ? this.selectedOu?._id : undefined;
    this.loading = true;
    this.loadError = false;
    try {
      const nodes = await this.getReportOuNodes();
      if (nodes.length === 0) {
        this.ous = [];
        this.applyFilters();
        return;
      }
      const res = await this.expertsService.getOverview(nodes.map((n) => n._id));
      if (res.error || !Array.isArray(res.data)) {
        this.loadError = true;
        return;
      }
      const byOu = new Map<string, OuExpertsOverview>(res.data.map((o: OuExpertsOverview) => [o.ou, o]));
      this.ous = nodes.map((node) => {
        const overview = byOu.get(node._id);
        return this.withSearchText({
          ...node,
          assigned: overview?.assigned || [],
          suggested: overview?.suggested || [],
          searchText: '',
        });
      });
      this.applyFilters();
      this.selectedOu =
        this.ous.find((o) => o._id === previousId) || this.filteredOus[0] || null;
    } catch (error) {
      console.error('content experts load failed', error);
      this.loadError = true;
    } finally {
      this.loading = false;
    }
  }

  /**
   * OUs the report page lets an admin pick: the CONTENT_UPDATE_LOGS tree,
   * narrowed to Ministries/Entities, flattened with each node's path.
   */
  private async getReportOuNodes(): Promise<Array<Omit<OuRow, 'assigned' | 'suggested' | 'searchText'>>> {
    const tree: any[] = (await this.permissionService.extractOUChildren('CONTENT_UPDATE_LOGS', 'r')) || [];
    let filtered = cloneDeep(tree)
      .map((node: any) => this.filterNodes(node))
      .filter((node: any) => node !== null);
    if (filtered.length === 0) {
      filtered = tree.filter(Boolean).map((item: any) => ({ ...item, children: [] }));
    }

    const rows: Array<Omit<OuRow, 'assigned' | 'suggested' | 'searchText'>> = [];
    const seen = new Set<string>();
    const walk = (node: any, trail: string[], depth: number) => {
      if (!node) return;
      // The tree also holds grouping nodes (the root and one per OU type) that
      // are not OUs; only real OU nodes carry `orgTypeId`.
      const isOu = !!node._id && 'orgTypeId' in node;
      if (isOu && !seen.has(node._id)) {
        seen.add(node._id);
        rows.push({
          _id: node._id,
          label: node.label || node.name || '',
          image: node.data || node.image || '',
          path: trail.join(' / '),
          depth,
        });
      }
      (node.children || []).forEach((child: any) => walk(child, [...trail, node.label || ''], depth + 1));
    };
    filtered.forEach((node: any) => walk(node, [], 0));
    return rows;
  }

  private filterNodes(node: any): any | null {
    if (!node) return null;
    if (Array.isArray(node.children) && node.children.length > 0) {
      node.children = node.children.map((c: any) => this.filterNodes(c)).filter((c: any) => c !== null);
    }
    if (node.tag === REPORT_OU_TAG || (Array.isArray(node.children) && node.children.length > 0)) {
      return node;
    }
    return null;
  }

  private withSearchText(row: OuRow): OuRow {
    const experts = row.assigned
      .map((a) => `${a.user?.fullName || ''} ${a.user?.email || ''}`)
      .join(' ');
    row.searchText = `${row.label} ${row.path} ${experts}`.toLowerCase();
    return row;
  }

  // ---------------------------------------------------------------------------
  // Filtering and selection
  // ---------------------------------------------------------------------------

  applyFilters(): void {
    const q = this.ouSearch.trim().toLowerCase();
    this.filteredOus = this.ous.filter((row) => {
      if (this.coverageFilter === 'covered' && this.activeCount(row) === 0) return false;
      if (this.coverageFilter === 'uncovered' && this.activeCount(row) > 0) return false;
      return !q || row.searchText.includes(q);
    });
  }

  clearSearch(): void {
    this.ouSearch = '';
    this.applyFilters();
  }

  selectOu(row: OuRow): void {
    this.selectedOu = row;
    // On narrow screens the detail panel sits below the list.
    if (window.innerWidth < 992) {
      setTimeout(() => this.detailPanel?.nativeElement.scrollIntoView({ behavior: 'smooth', block: 'start' }));
    }
  }

  trackById(_: number, item: { _id: string }): string {
    return item._id;
  }

  /** Assigned experts who can actually receive reports. */
  activeCount(row: OuRow): number {
    return row.assigned.filter((a) => a.user?.isActive).length;
  }

  get totalAssignments(): number {
    return this.ous.reduce((sum, row) => sum + row.assigned.length, 0);
  }

  get uniqueExperts(): number {
    const ids = new Set<string>();
    this.ous.forEach((row) => row.assigned.forEach((a) => a.user && ids.add(a.user._id)));
    return ids.size;
  }

  get coveredOus(): number {
    return this.ous.filter((row) => this.activeCount(row) > 0).length;
  }

  get unassignedSuggestions(): Array<ExpertUser & { isAssigned: boolean }> {
    return (this.selectedOu?.suggested || []).filter((s) => !s.isAssigned);
  }

  initials(name: string | undefined): string {
    const parts = (name || '').trim().split(/\s+/).filter(Boolean);
    return parts.slice(0, 2).map((p) => p.charAt(0)).join('') || '?';
  }

  checkisImg(path: string | undefined): boolean {
    return !!path && /\.(jpg|jpeg|png|gif|svg|webp)/i.test(path);
  }

  get isReadOnly(): boolean {
    return !this.canAdd && !this.canChange && !this.canRemove;
  }

  isBusy(key: string): boolean {
    return this.busy.has(key);
  }

  // ---------------------------------------------------------------------------
  // Mutations
  // ---------------------------------------------------------------------------

  /** Replaces one OU's data with what the server returned after a change. */
  private applyOverview(overview: OuExpertsOverview | undefined, ouId: string): void {
    const row = this.ous.find((o) => o._id === ouId);
    if (!row || !overview) return;
    row.assigned = overview.assigned || [];
    row.suggested = overview.suggested || [];
    this.withSearchText(row);
    this.applyFilters();
  }

  private async run(key: string, action: () => Promise<any>): Promise<void> {
    if (this.busy.has(key)) return;
    this.busy.add(key);
    try {
      await action();
    } finally {
      this.busy.delete(key);
    }
  }

  private toastError(res: any, fallback: string): void {
    const detail = res?.msg ? this.t(res.msg) : this.t(fallback);
    this.messageService.add({ severity: 'error', summary: this.t('Error'), detail });
  }

  assignSuggested(user: ExpertUser): void {
    const ou = this.selectedOu;
    if (!ou || !this.canAdd) return;
    this.run(`assign:${ou._id}:${user._id}`, async () => {
      const res = await this.expertsService.assign(ou._id, [user._id]);
      if (res.error) {
        this.toastError(res, 'Failed to assign expert');
        return;
      }
      this.applyOverview(res.data, ou._id);
      this.messageService.add({
        severity: 'success',
        summary: this.t('Success'),
        detail: this.tf('{name} was assigned as report expert', { name: user.fullName }),
      });
    });
  }

  assignAllSuggested(): void {
    const ou = this.selectedOu;
    const users = this.unassignedSuggestions;
    if (!ou || !this.canAdd || users.length === 0) return;
    this.confirmationService.confirm({
      key: 'experts',
      header: this.t('Assign all suggested experts'),
      message: this.tf('Assign {count} suggested experts to {ou}?', { count: users.length, ou: ou.label }),
      icon: 'pi pi-users',
      acceptLabel: this.t('Assign'),
      rejectLabel: this.t('Cancel'),
      accept: () =>
        this.run(`assign-all:${ou._id}`, async () => {
          const res = await this.expertsService.assign(ou._id, users.map((u) => u._id));
          if (res.error) {
            this.toastError(res, 'Failed to assign expert');
            return;
          }
          this.applyOverview(res.data, ou._id);
          this.messageService.add({ severity: 'success', summary: this.t('Success'), detail: this.t('Experts assigned successfully') });
        }),
    });
  }

  confirmRemove(assignment: ExpertAssignment): void {
    const ou = this.selectedOu;
    if (!ou || !this.canRemove) return;
    const name = assignment.user?.fullName || this.t('Deleted user');
    this.confirmationService.confirm({
      key: 'experts',
      header: this.t('Remove report expert'),
      message: this.tf('Remove {name} as report expert for {ou}? They will no longer receive or sign this unit reports. Their user account is not affected.', { name, ou: ou.label }),
      icon: 'pi pi-exclamation-triangle',
      acceptLabel: this.t('Remove expert'),
      rejectLabel: this.t('Cancel'),
      acceptButtonStyleClass: 'p-button-danger',
      accept: () =>
        this.run(`remove:${assignment._id}`, async () => {
          const res = await this.expertsService.remove(assignment._id);
          if (res.error) {
            this.toastError(res, 'Failed to remove expert');
            // The list may be stale (someone else removed it); resync this OU.
            await this.reloadOu(ou._id);
            return;
          }
          await this.reloadOu(ou._id);
          this.messageService.add({ severity: 'success', summary: this.t('Success'), detail: this.t('Expert removed successfully') });
        }),
    });
  }

  private async reloadOu(ouId: string): Promise<void> {
    const res = await this.expertsService.getOverview([ouId]);
    if (!res.error && Array.isArray(res.data)) {
      this.applyOverview(res.data[0], ouId);
    }
  }

  // ---------------------------------------------------------------------------
  // User picker (add / change)
  // ---------------------------------------------------------------------------

  openPicker(replacing: ExpertAssignment | null = null): void {
    if (!this.selectedOu || !(replacing ? this.canChange : this.canAdd)) return;
    this.pickerReplacing = replacing;
    this.pickerQuery = '';
    this.pickerResults = [];
    this.pickerSelected = [];
    this.pickerSearchError = false;
    this.pickerVisible = true;
    this.runUserSearch('');
  }

  onPickerQuery(value: string): void {
    this.pickerQuery = value;
    this.pickerSearch$.next(value.trim());
  }

  private async runUserSearch(query: string): Promise<void> {
    const requestId = ++this.pickerRequestId;
    this.pickerSearching = true;
    this.pickerSearchError = false;
    const res = await this.expertsService.searchUsers(query);
    // Drop responses for queries the user already typed past.
    if (requestId !== this.pickerRequestId) return;
    this.pickerSearching = false;
    if (res.error || !Array.isArray(res.data)) {
      this.pickerSearchError = true;
      this.pickerResults = [];
      return;
    }
    this.pickerResults = res.data;
  }

  isAlreadyAssigned(user: ExpertUser): boolean {
    return !!this.selectedOu?.assigned.some((a) => a.user?._id === user._id);
  }

  isSuggested(user: ExpertUser): boolean {
    return !!this.selectedOu?.suggested.some((s) => s._id === user._id);
  }

  isPicked(user: ExpertUser): boolean {
    return this.pickerSelected.some((u) => u._id === user._id);
  }

  togglePick(user: ExpertUser): void {
    if (this.isAlreadyAssigned(user)) return;
    if (this.pickerReplacing) {
      this.pickerSelected = this.isPicked(user) ? [] : [user];
      return;
    }
    this.pickerSelected = this.isPicked(user)
      ? this.pickerSelected.filter((u) => u._id !== user._id)
      : [...this.pickerSelected, user];
  }

  savePicker(): void {
    const ou = this.selectedOu;
    if (!ou || this.pickerSelected.length === 0) return;
    const replacing = this.pickerReplacing;
    this.run('picker', async () => {
      const res = replacing
        ? await this.expertsService.reassign(replacing._id, this.pickerSelected[0]._id)
        : await this.expertsService.assign(ou._id, this.pickerSelected.map((u) => u._id));
      if (res.error) {
        this.toastError(res, replacing ? 'Failed to update expert' : 'Failed to assign expert');
        return;
      }
      this.applyOverview(res.data, ou._id);
      this.pickerVisible = false;
      this.messageService.add({
        severity: 'success',
        summary: this.t('Success'),
        detail: this.t(replacing ? 'Expert updated successfully' : 'Experts assigned successfully'),
      });
    });
  }
}
